/**
 * WEB-ATTR-2026-001 — the app's half of web acquisition attribution.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE IS FOR
 *
 * `atlas-app.org` captures where a visitor came from and hands it to `web.atlas-app.org` two ways:
 * a first-party cookie on the shared registrable domain, and an `?atlas_attr=` token on the signup
 * link. This file reads whichever arrived, folds in this page's own URL when it carries a campaign,
 * carries the result onto `user_signed_up`, and posts it so the organization that signup creates can
 * be joined to the click that produced it.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * THE MODEL IS IMPORTED, NOT RESTATED — AND THE SECOND COPY OF THE STATE IS GONE
 *
 * Parsing, qualification, the TTL, the fold rule and the serialization live in `attributionCore.js`.
 * They used to be duplicated here, and the two copies drifted into real misattributions.
 *
 * ⚠️ MORE IMPORTANTLY, THIS FILE USED TO KEEP ITS OWN COPY OF THE STATE in AsyncStorage, alongside
 * the cookie the marketing site writes. Two copies of one fact meant the cookie could re-present a
 * touch this device had already folded, which is the ONLY way a stale touch could ever reach
 * `applyTouch` — and closing that took SEVEN review rounds: a monotonic clock guard, an escape that
 * inverted it, a clamp that froze it, a clamp that handed `last` to the older of two future stamps,
 * then an identity check that needed a history, that needed both carrier ids, that needed a ninth
 * slot. Every one of those was a real misattribution.
 *
 * There is now ONE copy. The cookie (mirrored to `localStorage` for a browser that refuses cookies,
 * holding the identical payload) is the single persistence path, and this file writes it as well as
 * reads it. A stale duplicate cannot exist, so none of that machinery does either.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ANALYTICS IS NEVER LOAD-BEARING (ATL-901). Everything here resolves; nothing throws outward. A
 * till must open, and a signup must complete, when every one of these steps has failed.
 */
import AsyncStorage from '@react-native-async-storage/async-storage'

import {
  ATTRIBUTION_TTL_DAYS,
  applyTouch,
  buildCookie,
  isExpired,
  isQualifying,
  parseTouch,
  pruneExpired,
  readCookie,
  readState,
  serialize,
  touchAgeDays,
} from './attributionCore'

export {
  ATTRIBUTION_COOKIE,
  ATTRIBUTION_QUERY_PARAM,
  ATTRIBUTION_TTL_DAYS,
  ATTRIBUTION_FIELDS,
  MAX_SERIALIZED_BYTES,
  applyTouch,
  deserialize,
  hostMatches,
  isExpired,
  isQualifying,
  parseTouch,
  readCookie,
  readState,
  serialize,
} from './attributionCore'

/** Same-origin mirror of the cookie payload, for a browser that refuses cookies entirely. */
export const WEB_ACQUISITION_MIRROR_KEY = 'atlas.growth.webAcquisition.v2'
/**
 * What this device last wrote to the carriers, and what they held before it did.
 *
 * ⚠️ THE ONLY REASON THIS IS PERSISTED IS THAT A COOKIE WRITE CAN FAIL WITHOUT THROWING. `persist`'s
 * `try/catch` sees a write that RAISES; a browser, extension or policy that drops a
 * `document.cookie` assignment does so silently. `readState` prefers the cookie over the mirror, so
 * a dropped write leaves the stale cookie winning over a fresher mirror — and the next `persist`
 * writes it back, destroying an acquisition this device had already captured.
 *
 * An in-memory version of this signal only covered re-resolves inside ONE document; a new page load
 * started with no memory of what had been written and the loss happened one load later. It is not a
 * second copy of the STATE — it holds no touches and is never read as attribution. It answers one
 * question the carriers cannot: did our own last write actually land?
 */
export const WEB_ACQUISITION_WRITE_LOG_KEY = 'atlas.growth.webAcquisitionWriteLog.v1'
export const WEB_ACQUISITION_LINK_STORAGE_KEY = 'atlas.growth.webAcquisitionLink.v1'
export const WEB_ACQUISITION_LINK_STATE_KEY = 'atlas.growth.webAcquisitionLinkState.v1'

/** Same bounds as the Apple Ads link path, for the same reason — see `linkInstallToOrganization`. */
export const REQUEST_TIMEOUT_MS = 15000
export const LINK_MAX_ATTEMPTS = 8
export const LINK_MIN_RETRY_INTERVAL_MS = 5 * 60 * 1000
export const LINK_EXHAUSTED_RETRY_INTERVAL_MS = 24 * 60 * 60 * 1000

/**
 * How many succeeded latches and attempt records are kept, newest first.
 *
 * EVICTING THE OLDEST IS SAFE: this client only ever posts its CURRENT pair, whose latch is by
 * construction the most recently appended, so a newest-first cap can never evict the entry that is
 * actually suppressing a request. 32 is far above any realistic count and bounds the key.
 */
export const LINK_LATCH_MAX_ENTRIES = 32

/**
 * Everything this module needs from the page, captured once, at module load.
 *
 * `null` on native (there is no `window.location`), which is the whole platform gate: web
 * acquisition is a web fact, and native installs are measured by Apple Ads attribution instead.
 */
export function snapshotInitialLocation(win) {
  const w = win ?? (typeof window !== 'undefined' ? window : null)
  if (!w || !w.location || typeof w.location.href !== 'string') return null
  const doc = w.document || (typeof document !== 'undefined' ? document : null)

  // ⚠️ `hostname` AND `secure` ARE PART OF THE SNAPSHOT, AND LEAVING THEM OUT PUT THE SECOND COPY
  // OF THE STATE BACK.
  //
  // `persist` scopes the cookie with `buildCookie(token, {hostname: location.hostname})`, and this
  // function is the ONLY `location` production ever supplies. Without these two fields
  // `cookieDomainFor(undefined)` answered null, so on `web.atlas-app.org` the app wrote a HOST-ONLY
  // `atlas_attr` beside the marketing site's `Domain=.atlas-app.org` one — two cookies of the same
  // name. `readCookie` keeps returning the site's (equal Path, earlier creation), so the app could
  // neither update nor expire the carrier it reads: a paid click captured on the app was folded,
  // written, and silently rewound on the next page load. That second copy is exactly what this
  // module's clock-free ordering argument depends on NOT existing.
  //
  // Both are derived from `href`, because that is the same field `parseTouch` already takes
  // `landing_host` from — one URL, one answer, rather than three fields that can disagree.
  let hostname = null
  let secure = true
  try {
    const url = new URL(w.location.href)
    hostname = url.hostname || null
    secure = url.protocol === 'https:'
  } catch {
    // A location that will not parse still has to yield a cookie the browser accepts. Falling back
    // to `Secure` is the safe direction: a cookie dropped on a plain-http dev server costs a local
    // test, and one sent in the clear on production costs a visitor's carrier.
    hostname = typeof w.location.hostname === 'string' ? w.location.hostname || null : null
    secure = w.location.protocol !== 'http:'
  }

  const snapshot = {
    href: w.location.href,
    hostname,
    secure,
    // `document.referrer` is a property of the DOCUMENT, not of the navigation — it does not change
    // for the life of the page, so freezing it loses nothing.
    referrer: doc && typeof doc.referrer === 'string' ? doc.referrer : '',
  }

  // ⚠️ THE COOKIE IS READ LIVE, AND FREEZING IT PUT BACK THE SECOND COPY OF THE STATE.
  //
  // `href` MUST be captured once: React Navigation replaces the address bar as soon as it resolves
  // the initial route and would take the `?atlas_attr=` handoff with it. `document.cookie` is the
  // opposite — `persist` writes to the LIVE document while the resolve read this snapshot, so the
  // app read one copy of the state and wrote another, and re-reading was never idempotent however
  // often the docblocks said it was.
  //
  // The rewind that reaches is an ordinary tab switch, not an edge case: on web,
  // react-native-web's `AppState` emits `change`→`active` on every `visibilitychange`, so the
  // bridge re-resolves. A paid click the marketing site folded into the SHARED `.atlas-app.org`
  // cookie in another tab was overwritten by the page-load value seconds after the user switched
  // back — destroyed in every carrier, before signup, with nothing reporting it.
  //
  // A getter rather than a second field so `location` stays one object with one meaning, and so a
  // caller cannot read a stale copy by holding onto it.
  Object.defineProperty(snapshot, 'cookie', {
    enumerable: true,
    get: () => (doc && typeof doc.cookie === 'string' ? doc.cookie : ''),
  })

  return snapshot
}

let initialLocation = null
try {
  initialLocation = snapshotInitialLocation()
} catch {
  initialLocation = null
}

/**
 * This page load's own touch, and whether it has already been FOLDED.
 *
 * ⚠️ THE WHOLE TOUCH IS MEMOISED, NOT JUST ITS ID, AND THE FOLD HAPPENS ONCE. An earlier version
 * memoised only the id, so the same touch was re-stamped with `now` on every resolve — that
 * browser's attribution could never reach the 90-day window, and since `FunnelScreen` re-resolves
 * at signup, every age this feature reports for the ad-straight-to-app population was measured from
 * the SIGNUP instead of from the click.
 *
 * The `folded` flag is the more important half. `isQualifying(own)` routes straight to `applyTouch`,
 * which sets `last = own` unconditionally, so on a page whose own url or referrer qualifies every
 * foreground reset `last` back to the landing touch and persisted that over the shared
 * `.atlas-app.org` cookie — destroying a paid click the marketing site had folded in from another
 * tab. react-native-web fires `AppState → active` on every `visibilitychange`, so a tab switch was
 * enough, and the server's backwards-move guard cannot see it: the posted last touch differs from
 * the first, so it looks like an ordinary move.
 *
 * Same shape, and the same reason, as `referrerConsumedForThisDocument` in `AttributionTracker.tsx`:
 * an observation belongs to the page LOAD, and re-presenting it later is not a new observation.
 */
let ownTouchMemo = { location: null, touch: undefined, folded: false, priorToken: null, wroteToken: null }

/** Test seam so a suite can drive a fresh page load. Production never calls this. */
export function __resetOwnTouchMemoForTests() {
  ownTouchMemo = { location: null, touch: undefined, folded: false, priorToken: null, wroteToken: null }
}

/** The snapshot taken at module load. Exposed so the bridge and tests read the same thing. */
export function getInitialLocation() {
  return initialLocation
}

/** Test seam. Production never calls this. */
export function __setInitialLocationForTests(value) {
  initialLocation = value
  __resetOwnTouchMemoForTests()
}



/**
 * Read the one stored state, fold in this page load's own URL when it qualifies, and persist.
 *
 * ⚠️ THE APP WRITES THE COOKIE TOO, AND THAT IS WHAT COLLAPSED THIS FUNCTION. While the app kept a
 * separate AsyncStorage copy, the cookie was a second, older description of the same fact — so
 * every resolve had to decide whether the carrier was new, which is the question seven review
 * rounds failed to answer safely. Writing back to the one carrier removes the question: after this
 * runs, the cookie IS the state, and re-reading it is idempotent.
 *
 * The own-URL touch is minted at most once per page load (`ownTouch`), because `parseTouch` mints a
 * fresh id on every call and the bridge re-resolves on every foreground — without that, a till left
 * open would write a new touch, and post a new row, every time it was foregrounded.
 */
export async function resolveWebAcquisition({
  location = getInitialLocation(),
  storage = AsyncStorage,
  now = () => Date.now(),
  mintId,
  ttlDays = ATTRIBUTION_TTL_DAYS,
  writeCookie,
} = {}) {
  try {
    if (!location) return null
    // ONE clock reading for the whole resolve: two readings can straddle the ninetieth-day boundary.
    const nowMs = now()

    let mirror = null
    try {
      mirror = await storage.getItem(WEB_ACQUISITION_MIRROR_KEY)
    } catch {
      mirror = null
    }

    const writeLog = await readWriteLog(storage)
    const cookieToken = readCookie(location.cookie) || ''

    // ⚠️ IS THIS BROWSER ACCEPTING COOKIE WRITES AT ALL? A cookie still holding EXACTLY what it held
    // before this device's last write, when that write intended something else and the mirror holds
    // that something else, is a cookie the write did not reach. Preferring it — which `readState`
    // does by default, and rightly, since it is the carrier the marketing site shares — would let a
    // stale value beat the fresher mirror and then be written back over it.
    //
    // Every other shape is a REAL write and is respected: a cookie changed by the marketing site in
    // another tab does not equal `prior`, so it wins, exactly as it should.
    //
    // The `mirror === wrote` clause is belt-and-braces: it separates "the cookie write failed" from
    // "BOTH writes failed", and the two cannot diverge in practice — the mirror and this log are
    // adjacent writes to the same storage, so a mirror failure takes the log with it and leaves an
    // older pair that still compares correctly. Removing it fails no test, by construction rather
    // than by omission.
    const cookieWriteNotLanding =
      Boolean(writeLog) &&
      cookieToken === writeLog.prior &&
      cookieToken !== writeLog.wrote &&
      (mirror ?? '') === writeLog.wrote

    // The carrier's CURRENT payload, in the order `readState` will consult it, asked before the
    // window is applied — see the cleanup branch below for why "was it there" and "was it still
    // valid" are different questions.
    const carrierToken = cookieWriteNotLanding ? (mirror ?? '') : cookieToken || mirror || ''
    const hadCarrier = Boolean(carrierToken)

    // ⚠️ DID THIS PAGE LOAD'S OWN WRITE ACTUALLY LAND? `persist` swallows a cookie write that
    // THROWS, but a browser, extension or policy that drops a `document.cookie` assignment does so
    // SILENTLY — the assignment is a no-op and nothing is raised. The fold latch then declined to
    // re-fold, `readState` kept preferring the stale cookie over the fresh mirror, and `persist`
    // overwrote the mirror with it: an acquisition captured and then destroyed in both carriers.
    //
    // "Somebody else moved the carrier" and "my own write never landed" are distinguishable without
    // a clock: a carrier still holding EXACTLY the value it held before this page load folded is
    // one that nothing has written to since. Anything else — including another tab's newer touch —
    // is a real write and must be respected.
    //
    // The `wroteToken` clause is belt-and-braces and cannot change the outcome: it differs from the
    // first two only when our fold produced the value that was already there, and re-folding a
    // no-op is a no-op. Removing it fails no test, by construction rather than by omission.
    const ownWriteWasLost =
      ownTouchMemo.folded &&
      carrierToken === ownTouchMemo.priorToken &&
      carrierToken !== ownTouchMemo.wroteToken

    // The clock is passed, not defaulted: `readState` drops a carrier whose window has closed, and
    // the ONE reading taken above is what keeps that decision consistent with the fold below.
    const carried = readState({
      href: location.href,
      cookie: cookieWriteNotLanding ? null : location.cookie,
      mirror,
      now: nowMs,
      ttlDays,
    })
    const own = ownTouchFor(location, nowMs, mintId)

    // A bare load with no campaign data and no external referrer is an existing user opening their
    // till, not a fresh acquisition — minting a "direct" first touch for it would file every
    // returning cashier as one.
    //
    // ⚠️ AND A QUALIFYING ONE IS FOLDED ONCE PER PAGE LOAD. See `ownTouchMemo`: re-folding on every
    // foreground drove `last` back to the landing touch and erased whatever the shared cookie had
    // acquired since.
    let next
    if (isQualifying(own) && (!ownTouchMemo.folded || ownWriteWasLost)) {
      next = applyTouch(carried, own, { now: nowMs, ttlDays })
      ownTouchMemo.folded = true
      ownTouchMemo.priorToken = carrierToken
      ownTouchMemo.wroteToken = next ? serialize(next) : ''
    } else {
      next = pruneExpired(carried, nowMs, ttlDays)
      // ⛔ THE LATCH MUST NOT DELETE THE POPULATION IT PROTECTS. A browser refusing BOTH carriers
      // has nothing for a re-fold to overwrite, and re-folding is the only thing keeping that
      // visitor's acquisition alive across foregrounds until they sign up. Guarded on the window,
      // so a page left open past the ninetieth day does not resurrect its own landing touch.
      if (!next && isQualifying(own) && !isExpired(own, nowMs, ttlDays)) {
        next = { first: own, last: own }
      }
    }

    if (!next) {
      // ⚠️ `carried` IS THE WRONG QUESTION, AND MAKING `readState` PRUNE MADE IT UNREACHABLE. Once
      // every carrier is dropped at the window, `carried` is already null for exactly the case this
      // branch exists to clean up — so an expired payload was left sitting in the cookie and the
      // mirror. `persist` refreshes `Max-Age` to 90 days on every live resolve, so it could outlive
      // its declared window by another 90. Harmless to read (every read prunes) and still wrong:
      // this module states that the TTL and the cookie's own max-age "cannot disagree about when
      // attribution ends", and retention is a promise about the browser, not about the reader.
      // The question is whether a carrier was THERE, not whether it was still valid.
      if (hadCarrier) await persist(storage, location, null, ttlDays, writeCookie, cookieToken)
      return null
    }
    await persist(storage, location, next, ttlDays, writeCookie, cookieToken)
    return next
  } catch (error) {
    console.warn('[growth] web acquisition resolve failed; app continues', error)
    return null
  }
}

/**
 * One own-URL touch per page load.
 *
 * Keyed on the location SNAPSHOT OBJECT, which production takes once at module load, so identity is
 * exactly "the same page load". A genuinely new load — and every test — builds a fresh object and
 * mints, which is correct for both.
 */
function ownTouchFor(location, nowMs, mintId) {
  if (ownTouchMemo.location !== location) {
    ownTouchMemo = { location, touch: undefined, folded: false }
  }
  if (ownTouchMemo.touch === undefined) {
    ownTouchMemo.touch = parseTouch({
      href: location.href,
      referrer: location.referrer,
      now: () => nowMs,
      mintId: mintId ?? mintOwnId,
    })
  }
  return ownTouchMemo.touch
}
function mintOwnId() {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  const bytes = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}


/**
 * Read the write log, with every value coerced back to the two strings this module reasons about.
 *
 * An unreadable log reads as ABSENT, which restores the old cookie-first behaviour — the safe
 * direction, since the log can only ever demote the cookie.
 */
async function readWriteLog(storage) {
  try {
    const raw = await storage.getItem(WEB_ACQUISITION_WRITE_LOG_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed.prior !== 'string' || typeof parsed.wrote !== 'string') return null
    return parsed
  } catch {
    return null
  }
}

/** Write the ONE state to the ONE carrier, plus its same-origin mirror. Never throws outward. */
async function persist(storage, location, state, ttlDays, writeCookie, priorCookieToken = '') {
  const token = state ? serialize(state) : ''
  const write =
    writeCookie ??
    ((cookie) => {
      if (typeof document !== 'undefined') document.cookie = cookie
    })
  try {
    write(
      state && token
        ? buildCookie(token, { hostname: location.hostname, ttlDays, secure: location.secure !== false })
        : buildCookie('', { hostname: location.hostname, ttlDays: 0, secure: location.secure !== false }),
    )
  } catch {
    /* a browser refusing a cookie must not break the resolve */
  }
  try {
    if (state && token) await storage.setItem(WEB_ACQUISITION_MIRROR_KEY, token)
    else await storage.removeItem?.(WEB_ACQUISITION_MIRROR_KEY)
  } catch (error) {
    console.warn('[growth] web acquisition mirror could not be persisted', error)
  }
  // Recorded AFTER both writes, so it describes what was actually attempted. If this fails, the
  // next resolve simply falls back to preferring the cookie.
  try {
    await storage.setItem(
      WEB_ACQUISITION_WRITE_LOG_KEY,
      JSON.stringify({ prior: priorCookieToken, wrote: token }),
    )
  } catch (error) {
    console.warn('[growth] web acquisition write log could not be persisted', error)
  }
}

/**
 * How long the SIGNUP PATH will wait for a resolve before giving up on the properties.
 *
 * ⚠️ THIS BOUND IS NOT DEFENSIVE. `resolveWebAcquisition` awaits `AsyncStorage.getItem` and then
 * two writes, and a storage call that never SETTLES never rejects — so neither its own `try/catch`
 * nor a `.catch()` at the call site ever runs. On the signup screen that await sits between a
 * SUCCEEDED `createNewUserAndOrganization` and the navigation away from the spinner: the account
 * and the organization exist, and the person is left looking at a spinner for the life of the
 * process. `analyticsIdentity.js` caps the very next line for exactly this reason
 * (`IDENTITY_READY_CAP_MS`, ATL-889); this is the same cap for the same shape, and it is what makes
 * "analytics is never load-bearing" true of the one screen where it would cost a customer.
 *
 * 2000ms, matching `IDENTITY_READY_CAP_MS`. The two are SEQUENTIAL on the signup screen, so the
 * worst case there is ~4s of spinner after a succeeded signup. Stated rather than glossed: an
 * earlier version of this comment claimed the two "should not add up" while the code awaited them
 * one after the other. Four bounded seconds on a screen that has just done real work is the trade;
 * an unbounded wait is a lost customer.
 */
export const RESOLVE_CAP_MS = 2000

/**
 * `resolveWebAcquisition`, bounded — resolving to null rather than hanging.
 *
 * First one wins, and the timer is cleared either way. A resolve that lands after the cap is simply
 * ignored here; it has already persisted whatever it folded, so the NEXT run (the bridge's, on the
 * next foreground) posts it. Nothing is lost but this one event's properties.
 */
export function resolveWebAcquisitionBounded(options = {}, capMs = RESOLVE_CAP_MS) {
  return new Promise((resolve) => {
    let settled = false
    const finish = (value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(value)
    }
    const timer = setTimeout(() => {
      console.warn(`[growth] web acquisition did not resolve within ${capMs}ms; signup continues`)
      finish(null)
    }, capMs)
    try {
      resolveWebAcquisition(options).then(finish, (error) => {
        console.warn('[growth] web acquisition resolve rejected; signup continues', error)
        finish(null)
      })
    } catch (error) {
      console.warn('[growth] web acquisition resolve threw synchronously; signup continues', error)
      finish(null)
    }
  })
}
/**
 * The non-PII properties that ride on `user_signed_up`.
 *
 * ⛔ `gclid` IS DELIBERATELY NOT AMONG THEM — only `web_attribution_has_gclid`. A gclid identifies a
 * single ad click, which is close enough to a session to keep in the first-party database (where an
 * offline conversion import would need it) and out of the analytics product. The five UTM
 * parameters ARE sent, under their conventional names, so PostHog's own campaign breakdowns work
 * without a join.
 *
 * `web_attribution_id` is the stable join: it is the first touch's id, and it is the primary key of
 * `analytics.web_acquisition_touch`. Given a PostHog cohort you can reach the row, and given the row
 * you can reach the organization — in both directions, without a person-level join.
 *
 * @param {{first: object, last: object} | null | undefined} state
 * @param {{now?: number, platform?: string}} [options]
 * @returns {Record<string, unknown>}
 */
export function buildAcquisitionProperties(state, { now = Date.now(), platform } = {}) {
  // `signup_platform` is emitted even with no attribution at all, on every platform: it is the
  // DENOMINATOR of the completeness query. Without it "web signups missing attribution" and "native
  // signups, which never have any" are the same number, and the ≥99% target is unmeasurable.
  const base = platform ? { signup_platform: platform } : {}
  if (!state?.first) return base

  const first = state.first
  const last = state.last || first
  return {
    ...base,
    web_attribution_id: first.touch_id,
    web_attribution_last_id: last.touch_id,
    web_attribution_has_gclid: Boolean(first.gclid),
    web_attribution_last_has_gclid: Boolean(last.gclid),
    web_attribution_captured_at: first.captured_at,
    web_attribution_age_days: Math.round(touchAgeDays(first, now) * 100) / 100,
    web_attribution_landing_host: first.landing_host,
    web_attribution_landing_path: first.landing_path,
    web_attribution_referrer_host: first.referrer_host,
    web_attribution_capture_origin: first.capture_origin,
    utm_source: first.utm_source,
    utm_medium: first.utm_medium,
    utm_campaign: first.utm_campaign,
    utm_term: first.utm_term,
    utm_content: first.utm_content,
  }
}

/**
 * Post the touches and the organization link to the backend.
 *
 * Bounded exactly like `linkInstallToOrganization` (ASO-ATTR-001), and for the reason that path
 * learned the hard way: this runs from an effect that re-fires on every foreground, so a persistent
 * failure — a backend outage, an organization still Firestore-only at signup, which the endpoint
 * answers 503 for — would otherwise be one POST and one Sentry event per foreground on a till that
 * is opened dozens of times a day. Past the fast budget it drops to once per 24h rather than
 * stopping: a cap that gives up permanently converts a transient outage into attribution that is
 * lost for good, and there is no server-side reconciliation for a touch that never arrived.
 *
 * BOOKKEEPING IS WRITTEN BEFORE THE REQUEST. A timeout or a dead network REJECTS, and the rejection
 * skips every statement after the call — so a counter incremented afterwards never engages for the
 * failure it exists to bound.
 */
export async function postWebAcquisition({
  state,
  organizationId,
  apiBaseUrl,
  getAuthToken,
  platform,
  storage = AsyncStorage,
  fetchImpl = globalThis.fetch,
  now = () => Date.now(),
  onError,
}) {
  if (!state?.first || !organizationId || !apiBaseUrl) return { linked: false, reason: 'not-ready' }
  // ⚠️ THE LAST TOUCH IS PART OF THE LATCH KEY, AND LEAVING IT OUT MADE THE SERVER'S UPDATE ARM
  // UNREACHABLE. The first touch is write-once for 90 days, so a latch keyed on it alone answers
  // `already-linked` for every later call — including the one carrying a NEW last non-direct touch.
  // `web_acquisition_organization`'s `on conflict … do update set last_touch_id`, and the
  // `last_touch_channel` column the reporting view exposes beside it, could then never change after
  // the first successful link: a plausible-looking value frozen at signup. Keyed on the PAIR, a new
  // last touch is a new fact to send exactly once, and a repeat of the same pair still latches.
  const lastTouchId = state.last?.touch_id ?? state.first.touch_id
  const latchValue = `${state.first.touch_id}:${lastTouchId}:${organizationId}`
  try {
    const stored = await storage.getItem(WEB_ACQUISITION_LINK_STORAGE_KEY)
    if (typeof stored === 'string' && stored.split('|').includes(latchValue)) {
      return { linked: false, reason: 'already-linked' }
    }

    const attemptState = await readLinkState(storage)
    const entry = attemptState[latchValue] || { attempts: 0, lastAttemptAt: null }
    const elapsed = now() - (entry.lastAttemptAt ?? 0)
    if (entry.attempts >= LINK_MAX_ATTEMPTS) {
      // `elapsed < 0` is a clock that moved backwards; treating it as "too soon" delays one attempt
      // rather than opening an unthrottled loop.
      if (entry.lastAttemptAt == null || elapsed < 0 || elapsed < LINK_EXHAUSTED_RETRY_INTERVAL_MS) {
        return { linked: false, reason: 'attempts-exhausted' }
      }
    }
    if (entry.lastAttemptAt != null && elapsed < LINK_MIN_RETRY_INTERVAL_MS) {
      return { linked: false, reason: 'backoff' }
    }

    const token = await getAuthToken()
    // No session yet is not a failure and must not spend an attempt.
    if (!token) return { linked: false, reason: 'no-auth-token' }

    await writeLinkState(storage, pruneLinkState({
      ...attemptState,
      [latchValue]: { attempts: entry.attempts + 1, lastAttemptAt: now() },
    }, latchValue))

    const body = {
      firstTouch: state.first,
      // Sent only when it is genuinely a different touch; the backend defaults it to the first.
      ...(state.last && state.last.touch_id !== state.first.touch_id ? { lastTouch: state.last } : {}),
      ...(platform ? { platform } : {}),
    }

    const response = await postWithTimeout(fetchImpl, `${apiBaseUrl}/attribution/web/link`, body, {
      Authorization: `Bearer ${token}`,
    })

    if (!response?.ok) {
      onError?.(new Error(`web acquisition link failed with HTTP ${response?.status}`))
      return { linked: false, reason: `http-${response?.status ?? 'network'}` }
    }

    // ⚠️ A 200 IS NOT A STORED LINK, AND LATCHING ON THE STATUS ALONE MADE THE KILL SWITCH
    // DESTRUCTIVE. `WEB_ACQUISITION_ENABLED=false` is the documented FIRST rollback lever and takes
    // no deploy — and the endpoint answers it with HTTP 200 and `{linked: false, reason: '…is set
    // to false'}`, having written nothing. Latched, that reads as done forever: turning the switch
    // back on would never re-post, so every device that foregrounded during the disabled window
    // loses its acquisition permanently and silently. Refusing to latch leaves the device's own
    // bounded schedule (8 fast attempts, then daily) to pick it up once the switch returns.
    //
    // Not an `onError`: a deliberate refusal is not a failure, and one Sentry event per foreground
    // on a till is exactly the noise this module's retry budget exists to avoid.
    if (await serverRefusedLink(response)) {
      return { linked: false, reason: 'refused' }
    }

    // Newest LAST, then keep the newest `LINK_LATCH_MAX_ENTRIES`. The current pair is the one just
    // appended, so it always survives — see LINK_LATCH_MAX_ENTRIES for why that makes eviction safe.
    const previous = typeof stored === 'string' && stored ? stored.split('|').filter(Boolean) : []
    const next = [...previous.filter((v) => v !== latchValue), latchValue]
      .slice(-LINK_LATCH_MAX_ENTRIES)
      .join('|')
    await storage.setItem(WEB_ACQUISITION_LINK_STORAGE_KEY, next)
    return { linked: true, reason: 'linked' }
  } catch (error) {
    onError?.(error)
    return { linked: false, reason: 'error' }
  }
}

/**
 * Did the server answer 200 while explicitly declining to store the link?
 *
 * ONLY an explicit `linked: false` counts. A body that is absent, unparseable, or shaped some other
 * way reads as STORED, and that asymmetry is deliberate: treating every unreadable 200 as a refusal
 * would turn a working pipeline behind a proxy that rewrites bodies into an unbounded retry loop,
 * which is a worse failure than the one this guards. The only 200-with-`linked:false` the endpoint
 * produces today is the `WEB_ACQUISITION_ENABLED=false` kill switch.
 */
async function serverRefusedLink(response) {
  try {
    if (typeof response.json !== 'function') return false
    const body = await response.json()
    return Boolean(body) && body.linked === false
  } catch {
    /* an unreadable body is not a refusal */
    return false
  }
}

/**
 * The attempt map, with every entry coerced back into the two numbers this module reasons about.
 *
 * ⚠️ AN UNREADABLE COUNTER DISARMS THE CAP IT IS SUPPOSED TO ENFORCE, and it does so silently.
 * `entry.attempts` reaching this code as anything but a number makes `attempts + 1` NaN, and from
 * then on `NaN >= LINK_MAX_ATTEMPTS` is false FOREVER: the 8-attempt budget and the 24-hour
 * fallback behind it never engage again for that pair, leaving only the 5-minute backoff. Against a
 * persistently failing backend that is a POST and a Sentry event every five minutes for the life of
 * the install — precisely the noise this module's whole retry budget exists to prevent. A
 * non-numeric `lastAttemptAt` is worse still: every `elapsed < …` comparison is false, so there is
 * no backoff at all and the request fires on every foreground.
 *
 * Same rule and same direction as `readSeenCarrierTouchIds`: a record that was hand-edited, written
 * by a different version, or half-written reads as "never attempted" rather than widening anything.
 * That costs at most one extra POST and re-arms the bound, which is the safe way round.
 */
async function readLinkState(storage) {
  try {
    const raw = await storage.getItem(WEB_ACQUISITION_LINK_STATE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out = {}
    for (const [key, value] of Object.entries(parsed)) {
      const attempts = value?.attempts
      const lastAttemptAt = value?.lastAttemptAt
      out[key] = {
        attempts: Number.isFinite(attempts) && attempts > 0 ? Math.floor(attempts) : 0,
        lastAttemptAt: Number.isFinite(lastAttemptAt) ? lastAttemptAt : null,
      }
    }
    return out
  } catch {
    return {}
  }
}

/**
 * Keep the attempt map bounded, newest-attempted first, and ALWAYS keep `keep`.
 *
 * `keep` is passed explicitly rather than inferred from the timestamps because the entry being
 * written is the one whose bookkeeping must survive: dropping it would reset the attempt counter
 * that bounds this very request, turning the cap into an unthrottled retry loop.
 */
export function pruneLinkState(state, keep) {
  const entries = Object.entries(state)
  if (entries.length <= LINK_LATCH_MAX_ENTRIES) return state
  const ordered = entries.sort((a, b) => (b[1]?.lastAttemptAt ?? 0) - (a[1]?.lastAttemptAt ?? 0))
  const kept = ordered.slice(0, LINK_LATCH_MAX_ENTRIES)
  if (keep && !kept.some(([k]) => k === keep) && state[keep]) {
    kept[kept.length - 1] = [keep, state[keep]]
  }
  return Object.fromEntries(kept)
}

async function writeLinkState(storage, state) {
  try {
    await storage.setItem(WEB_ACQUISITION_LINK_STATE_KEY, JSON.stringify(state))
  } catch (error) {
    console.warn('[growth] web acquisition link attempt state could not be persisted', error)
  }
}

async function postWithTimeout(fetchImpl, url, body, headers) {
  if (typeof fetchImpl !== 'function') throw new Error('fetch is unavailable')
  const controller = typeof AbortController === 'function' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null
  try {
    return await fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(headers || {}) },
      body: JSON.stringify(body),
      ...(controller ? { signal: controller.signal } : {}),
    })
  } finally {
    if (timer) clearTimeout(timer)
  }
}
